import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { debounceTime, distinctUntilChanged, finalize, merge, Observable, of, Subject, switchMap } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TableLazyLoadEvent, TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { SkeletonModule } from 'primeng/skeleton';
import { FloatLabelModule } from 'primeng/floatlabel';
import { SelectModule } from 'primeng/select';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { MenuModule } from 'primeng/menu';
import { TooltipModule } from 'primeng/tooltip';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { MenuItem } from 'primeng/api';
import { ToastrService } from 'ngx-toastr';
import {
  EntityAuditCompareDTO,
  EntityChangeDTO,
  WrapperPageableResponseEntityChangeDTO,
  WrapperResponseEntityAuditCompareDTO
} from '../../core/models';
import { EntityAuditService } from '../../services/entity-audit.service';
import { UserDTO, UserService } from '../../services/user.service';
import { JsonViewerComponent } from '../shared/json-viewer/json-viewer.component';

type UserFilterOption = UserDTO & { displayLabel: string };

@Component({
  selector: 'app-audit-list',
  templateUrl: './audit-list.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      :host {
        display: flex;
        flex-direction: column;
        flex: 1 1 0%;
        min-height: 0;
        width: 100%;
      }
      :host ::ng-deep .audit-primeng-table .p-datatable-thead > tr > th {
        background: rgb(248 250 252);
        border-color: rgb(226 232 240);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .audit-primeng-table .p-datatable-tbody > tr > td {
        padding: 0.4rem 0.45rem !important;
      }
      :host ::ng-deep .audit-primeng-table .p-datatable-table {
        width: 100%;
        table-layout: auto;
      }
      :host ::ng-deep p-table {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .audit-primeng-table.p-datatable,
      :host ::ng-deep .p-datatable {
        display: flex;
        flex-direction: column;
        height: 100%;
        min-height: 0;
      }
      :host ::ng-deep .p-datatable-table-container,
      :host ::ng-deep .p-datatable-flex-scrollable > .p-datatable-table-container {
        flex: 1 1 0;
        min-height: 0;
        overflow: auto;
      }
      :host ::ng-deep .p-datatable-paginator-bottom {
        flex-shrink: 0;
      }
      :host ::ng-deep .p-paginator .p-paginator-page.p-paginator-page-selected,
      :host ::ng-deep button.p-paginator-page-selected {
        background: rgb(224 242 254) !important;
        border-color: rgb(14 165 233) !important;
        color: rgb(2 132 199) !important;
      }
      :host ::ng-deep .audit-json-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .audit-json-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
      :host ::ng-deep .p-autocomplete {
        width: 100%;
      }
      :host ::ng-deep .p-autocomplete .p-autocomplete-input {
        width: 100%;
      }
    `
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatTooltipModule,
    TableModule,
    ButtonModule,
    SkeletonModule,
    FloatLabelModule,
    SelectModule,
    InputTextModule,
    IconFieldModule,
    InputIconModule,
    DatePickerModule,
    TagModule,
    DrawerModule,
    MenuModule,
    TooltipModule,
    AutoCompleteModule,
    JsonViewerComponent
  ]
})
export class AuditListComponent {
  private entityAuditService = inject(EntityAuditService);
  private userService = inject(UserService);
  private fb = inject(FormBuilder);
  private router = inject(Router);
  private toastr = inject(ToastrService);

  changes = signal<EntityChangeDTO[]>([]);
  loading = signal(false);
  initialLoadDone = signal(false);
  error = signal<string | null>(null);

  currentPage = signal(0);
  pageSize = signal(20);
  totalItems = signal(0);
  pageSizeOptions = [20, 50, 100];
  skeletonRows = [1, 2, 3, 4, 5, 6, 7, 8];
  hasActiveFilters = signal(false);

  selectedChange = signal<EntityChangeDTO | null>(null);
  drawerOpen = signal(false);
  compare = signal<EntityAuditCompareDTO | null>(null);
  compareLoading = signal(false);
  /** Historial de la entidad abierta en el drawer (más reciente primero). */
  entityTimeline = signal<EntityChangeDTO[]>([]);
  timelineIndex = signal(-1);

  canGoOlderVersion = computed(() => {
    const idx = this.timelineIndex();
    return idx >= 0 && idx < this.entityTimeline().length - 1;
  });
  canGoNewerVersion = computed(() => this.timelineIndex() > 0);

  rowMenuItems = signal<MenuItem[]>([]);

  private compareRequest$ = new Subject<{
    entityType: string;
    entityId: number;
    commitId: string;
    seed: EntityChangeDTO;
    reloadTimeline: boolean;
  }>();

  entityFilterOptions = [
    { value: 'Customer', label: 'Cliente' },
    { value: 'Product', label: 'Producto' },
    { value: 'Plan', label: 'Plan' },
    { value: 'Sale', label: 'Venta' },
    { value: 'Subscription', label: 'Suscripción' },
    { value: 'Payment', label: 'Pago' },
    { value: 'User', label: 'Usuario' }
  ];

  userFilterOptions = signal<UserFilterOption[]>([]);

  filtersForm = this.fb.group({
    entityType: this.fb.control<string | null>(null),
    entityId: [''],
    userSelection: this.fb.control<UserFilterOption | null>(null),
    commitDateRange: this.fb.control<Date[] | null>(null)
  });

  constructor() {
    this.filtersForm.controls.entityId.valueChanges.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      takeUntilDestroyed()
    ).subscribe(() => this.applyFilters());

    merge(
      this.filtersForm.controls.entityType.valueChanges,
      this.filtersForm.controls.commitDateRange.valueChanges,
      this.filtersForm.controls.userSelection.valueChanges
    ).pipe(takeUntilDestroyed()).subscribe(() => this.applyFilters());

    this.compareRequest$.pipe(
      switchMap((req): Observable<WrapperResponseEntityAuditCompareDTO> => {
        this.compareLoading.set(true);
        this.compare.set(null);
        const timeline$: Observable<WrapperPageableResponseEntityChangeDTO | null> = req.reloadTimeline
          ? this.entityAuditService.getChanges(req.entityType, req.entityId, { page: 0, size: 200 })
          : of(null);
        return timeline$.pipe(
          switchMap((timelineResponse) => {
            if (timelineResponse) {
              const timeline = timelineResponse.ok ? (timelineResponse.body ?? []) : [];
              this.entityTimeline.set(timeline);
              const idx = Math.max(0, timeline.findIndex((item) => this.sameCommitId(item.commitId, req.commitId)));
              this.timelineIndex.set(timeline.length ? idx : -1);
              if (timeline[idx]) {
                this.selectedChange.set({
                  ...req.seed,
                  ...timeline[idx],
                  entityType: req.entityType,
                  entityId: req.entityId
                });
              }
            } else {
              const timeline = this.entityTimeline();
              const idx = timeline.findIndex((item) => this.sameCommitId(item.commitId, req.commitId));
              if (idx >= 0) {
                this.timelineIndex.set(idx);
                this.selectedChange.set({
                  ...req.seed,
                  ...timeline[idx],
                  entityType: req.entityType,
                  entityId: req.entityId
                });
              }
            }
            return this.entityAuditService.compareWithPrevious(req.entityType, req.entityId, req.commitId);
          }),
          finalize(() => this.compareLoading.set(false))
        );
      }),
      takeUntilDestroyed()
    ).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.compare.set(response.body);
          const current = this.selectedChange();
          if (current) {
            this.selectedChange.set({
              ...current,
              commitId: response.body.commitId ?? current.commitId,
              author: response.body.author ?? current.author,
              commitDate: response.body.commitDate ?? current.commitDate,
              operation: response.body.operation ?? current.operation,
              version: response.body.version ?? current.version
            });
          }
        } else {
          this.toastr.error(response.message ?? 'Error al comparar versiones');
        }
      },
      error: () => this.toastr.error('Error al comparar versiones')
    });

    this.loadChanges();
  }

  loadChanges(): void {
    this.loading.set(true);
    this.error.set(null);

    const filters = this.buildFilters();
    this.entityAuditService.search(filters, {
      page: this.currentPage(),
      size: this.pageSize()
    }).subscribe({
      next: (response) => {
        if (response.ok) {
          this.changes.set(response.body ?? []);
          if (response.page) {
            this.totalItems.set(response.page.totalItems ?? 0);
            this.pageSize.set(response.page.pageSize ?? this.pageSize());
            this.currentPage.set(Math.max(0, (response.page.currentPage ?? 1) - 1));
          } else {
            this.totalItems.set(response.body?.length ?? 0);
          }
        } else {
          this.error.set(response.message || 'Error al cargar la auditoría');
        }
        this.loading.set(false);
        this.initialLoadDone.set(true);
      },
      error: (err) => {
        this.error.set(err.error?.message || 'Error al conectar con el servidor. Verifica que el API esté ejecutándose.');
        this.loading.set(false);
        this.initialLoadDone.set(true);
      }
    });
  }

  refreshTable(): void {
    this.loadChanges();
  }

  onLazyLoad(event: TableLazyLoadEvent): void {
    const rows = event.rows ?? this.pageSize();
    const first = event.first ?? 0;
    const page = rows ? Math.floor(first / rows) : 0;
    const size = rows || this.pageSize();
    if (page === this.currentPage() && size === this.pageSize() && this.initialLoadDone()) {
      return;
    }
    this.currentPage.set(page);
    this.pageSize.set(size);
    this.loadChanges();
  }

  applyFilters(): void {
    this.syncActiveFilters();
    this.currentPage.set(0);
    this.loadChanges();
  }

  clearFilters(): void {
    this.userFilterOptions.set([]);
    this.filtersForm.reset(
      { entityType: null, entityId: '', userSelection: null, commitDateRange: null },
      { emitEvent: false }
    );
    this.applyFilters();
  }

  onUserFilterSearch(event: { query: string }): void {
    const q = event.query?.trim() ?? '';
    if (q.length < 3) {
      this.userFilterOptions.set([]);
      return;
    }
    this.userService.getUsers({ 'q[lk]': q }, { page: 0, size: 20 }).subscribe({
      next: (response) => {
        const users = response.ok ? (response.body ?? []) : [];
        this.userFilterOptions.set(users.map((user) => ({
          ...user,
          displayLabel: this.userDisplayLabel(user)
        })));
      },
      error: () => this.userFilterOptions.set([])
    });
  }

  onUserFilterSelect(value: UserFilterOption | null): void {
    this.filtersForm.controls.userSelection.setValue(value, { emitEvent: true });
  }

  private userDisplayLabel(user: UserDTO): string {
    const parts = [user.name, user.lastname, user.surname].filter(Boolean);
    const name = parts.length ? parts.join(' ').trim() : user.email;
    return user.email ? `${name} (${user.email})` : name || '—';
  }

  openRowMenu(event: Event, menu: { toggle: (e: Event) => void }, change: EntityChangeDTO): void {
    event.stopPropagation();
    const items: MenuItem[] = [
      {
        label: 'Ver comparación',
        icon: 'pi pi-eye',
        command: () => this.openCompare(change)
      }
    ];
    const route = this.entityRoute(change);
    if (route) {
      items.push({
        label: 'Ir al registro',
        icon: 'pi pi-external-link',
        command: () => this.router.navigate(route)
      });
    }
    this.rowMenuItems.set(items);
    menu.toggle(event);
  }

  openCompare(change: EntityChangeDTO): void {
    this.selectedChange.set(change);
    this.compare.set(null);
    this.drawerOpen.set(true);
    if (!change.entityType || change.entityId == null || !change.commitId) {
      return;
    }
    this.compareRequest$.next({
      entityType: change.entityType,
      entityId: change.entityId,
      commitId: change.commitId,
      seed: change,
      reloadTimeline: true
    });
  }

  goToPreviousVersion(): void {
    if (!this.canGoOlderVersion()) {
      return;
    }
    const idx = this.timelineIndex() + 1;
    const target = this.entityTimeline()[idx];
    const current = this.selectedChange();
    if (!target?.commitId || !current?.entityType || current.entityId == null) {
      return;
    }
    this.timelineIndex.set(idx);
    this.compareRequest$.next({
      entityType: current.entityType,
      entityId: current.entityId,
      commitId: target.commitId,
      seed: { ...current, ...target },
      reloadTimeline: false
    });
  }

  goToNextVersion(): void {
    if (!this.canGoNewerVersion()) {
      return;
    }
    const idx = this.timelineIndex() - 1;
    const target = this.entityTimeline()[idx];
    const current = this.selectedChange();
    if (!target?.commitId || !current?.entityType || current.entityId == null) {
      return;
    }
    this.timelineIndex.set(idx);
    this.compareRequest$.next({
      entityType: current.entityType,
      entityId: current.entityId,
      commitId: target.commitId,
      seed: { ...current, ...target },
      reloadTimeline: false
    });
  }

  onDrawerVisibleChange(visible: boolean): void {
    this.drawerOpen.set(visible);
    if (!visible) {
      this.selectedChange.set(null);
      this.compare.set(null);
      this.entityTimeline.set([]);
      this.timelineIndex.set(-1);
    }
  }

  private sameCommitId(a?: string, b?: string): boolean {
    if (!a || !b) return false;
    if (a === b) return true;
    const na = Number(a);
    const nb = Number(b);
    return !Number.isNaN(na) && !Number.isNaN(nb) && na === nb;
  }

  copyJson(): void {
    const document = this.compare()?.document ?? {};
    navigator.clipboard.writeText(JSON.stringify(document, null, 2)).then(
      () => this.toastr.success('JSON copiado'),
      () => this.toastr.error('No se pudo copiar el JSON')
    );
  }

  entityLabel(entityType?: string): string {
    return this.entityFilterOptions.find((o) => o.value === entityType)?.label ?? entityType ?? '—';
  }

  entityIcon(entityType?: string): string {
    switch (entityType) {
      case 'Customer':
        return 'pi pi-users';
      case 'Product':
        return 'pi pi-box';
      case 'Plan':
        return 'pi pi-list';
      case 'Sale':
        return 'pi pi-shopping-cart';
      case 'Subscription':
        return 'pi pi-sync';
      case 'Payment':
        return 'pi pi-wallet';
      case 'User':
        return 'pi pi-user';
      default:
        return 'pi pi-history';
    }
  }

  entityColorClasses(entityType?: string): string {
    switch (entityType) {
      case 'Customer':
        return 'bg-sky-100 text-sky-600';
      case 'Product':
        return 'bg-violet-100 text-violet-600';
      case 'Plan':
        return 'bg-indigo-100 text-indigo-600';
      case 'Sale':
        return 'bg-emerald-100 text-emerald-600';
      case 'Subscription':
        return 'bg-teal-100 text-teal-600';
      case 'Payment':
        return 'bg-amber-100 text-amber-700';
      case 'User':
        return 'bg-fuchsia-100 text-fuchsia-600';
      default:
        return 'bg-slate-100 text-slate-600';
    }
  }

  operationLabel(operation?: string): string {
    switch (operation) {
      case 'CREATED':
        return 'Alta';
      case 'DELETED':
        return 'Baja';
      case 'UPDATED':
        return 'Cambio';
      default:
        return operation || '—';
    }
  }

  operationSeverity(operation?: string): 'success' | 'info' | 'danger' | 'secondary' {
    switch (operation) {
      case 'CREATED':
        return 'success';
      case 'DELETED':
        return 'danger';
      case 'UPDATED':
        return 'info';
      default:
        return 'secondary';
    }
  }

  private entityRoute(change: EntityChangeDTO): string[] | null {
    if (!change.entityType || change.entityId == null) return null;
    const map: Record<string, string> = {
      Customer: '/customers',
      Product: '/products',
      Plan: '/plans',
      Sale: '/sales',
      Subscription: '/subscriptions',
      User: '/users'
    };
    const base = map[change.entityType];
    return base ? [base, String(change.entityId)] : null;
  }

  private buildFilters(): {
    entityType?: string;
    entityId?: number;
    author?: string;
    from?: string;
    to?: string;
  } {
    const value = this.filtersForm.getRawValue();
    const filters: {
      entityType?: string;
      entityId?: number;
      author?: string;
      from?: string;
      to?: string;
    } = {};

    if (value.entityType) {
      filters.entityType = value.entityType;
    }

    const rawId = value.entityId?.trim();
    if (rawId) {
      const parsed = Number(rawId);
      if (Number.isInteger(parsed) && parsed > 0 && String(parsed) === rawId) {
        filters.entityId = parsed;
      }
    }

    const authorEmail = value.userSelection?.email?.trim();
    if (authorEmail) {
      filters.author = authorEmail;
    }

    const range = value.commitDateRange;
    if (range?.[0] && range[1]) {
      const from = new Date(range[0]);
      from.setHours(0, 0, 0, 0);
      const to = new Date(range[1]);
      to.setHours(23, 59, 59, 999);
      filters.from = from.toISOString();
      filters.to = to.toISOString();
    }

    return filters;
  }

  private syncActiveFilters(): void {
    const raw = this.filtersForm.getRawValue();
    this.hasActiveFilters.set(
      !!(
        raw.entityType ||
        raw.entityId?.trim() ||
        raw.userSelection ||
        (raw.commitDateRange?.[0] && raw.commitDateRange?.[1])
      )
    );
  }
}
